import { inject } from "aurelia";
import { ModelTypes, order_by } from '@zeus';
import { IImagineService } from './services/imagine-service';
import { aspectRatioData } from './components/data';
import { toastifyError, toastifySuccess } from '@helpers/toastify';
import { IAuthService } from "@interfaces/auth-service/auth-service-interface";
import { IDialogService } from "@aurelia/dialog";
import { GoPremiumDialog } from "@components/calendar/components/go-premium-dialog/go-premium-dialog";
import { FullScreenSpinnerDialog } from "@components/full-screen-spinner-dialog/full-screen-spinner-dialog";
import { GoogleAnalyticsEventAction, GoogleAnalyticsEventCategory, GoogleAnalyticsHelper } from "@helpers/google-analytics/google-analytics-helper";
import { IFormInputOption } from "@components/form/interfaces/form-interface";
import { IZeusClient, ZeusClient } from "@services/graphql-service/zeus-client";
import { MediaHistoryDrawer } from "./components/media-history/media-history-drawer";

export type ReuseParamsModel = {
  colorPalette: string
  artisticStyle: string
  perspective: string
  prompt: string
  aspectRatio: string
}

@inject(IImagineService, IZeusClient, IAuthService, IDialogService)
export class ImagineModule {
  private ilustration: string = null;
  private brand: ModelTypes['brands']
  private isLoading: boolean = false
  private fileId: string
  private medias: ModelTypes['userMedia'][] = []
  private colorPalette: IFormInputOption
  private artisticStyle: IFormInputOption
  private perspective: IFormInputOption
  private imagePrompt: string;
  private showLikeImage: boolean = false

  private colorPalettiesChipOptions: IFormInputOption[] = [
    { label: 'Minha marca', value: 'Minha marca' },
    { label: 'Tons quentes', value: 'Tons quentes' },
    { label: 'Monocromático', value: 'Monocromático' }
  ]
  private colorPalettiesOptions: IFormInputOption[] = [
    { label: "Cores quentes (vermelho, laranja, amarelo)", value: "Cores quentes (vermelho, laranja, amarelo)" },
    { label: "Cores frias (azul, verde, roxo)", value: "Cores frias (azul, verde, roxo)" },
    { label: "Monocromático (preto e branco)", value: "Monocromático (preto e branco)" },
    { label: "Pastel (tons suaves)", value: "Pastel (tons suaves)" },
    { label: "Neon (cores brilhantes e vibrantes)", value: "Neon (cores brilhantes e vibrantes)" },
    { label: "Alto contraste (cores fortes e opostas)", value: "Alto contraste (cores fortes e opostas)" },
    { label: "Sepia (tons marrons envelhecidos)", value: "Sepia (tons marrons envelhecidos)" },
    { label: "Tons metálicos (prata, dourado, bronze)", value: "Tons metálicos (prata, dourado, bronze)" },
    { label: "Gradientes (transições suaves de cores)", value: "Gradientes (transições suaves de cores)" },
    { label: "Cores naturais (verde floresta, azul céu, marrom terra)", value: "Cores naturais (verde floresta, azul céu, marrom terra)" },
    { label: "Tons neutros (bege, cinza, branco, preto)", value: "Tons neutros (bege, cinza, branco, preto)" }
  ]

  private artisticStyleChipOptions: IFormInputOption[] = [
    { label: 'Realista', value: 'Realista' },
    { label: 'Desenho', value: 'Desenho' },
    { label: 'Fotografia', value: 'Fotografia' }
  ]
  private artisticStyleOptions: IFormInputOption[] = [
    { label: 'Realista', value: 'Realista' },
    { label: 'Minimalista', value: 'Minimalista' },
    { label: 'Futurista', value: 'Futurista' },
    { label: 'Vintage', value: 'Vintage' },
    { label: 'Cartoon', value: 'Cartoon' },
    { label: 'Surrealista', value: 'Surrealista' },
    { label: 'Pintura a óleo', value: 'Pintura a óleo' },
    { label: 'Estilo aquarela', value: 'Estilo aquarela' },
    { label: 'Estilo 3D', value: 'Estilo 3D' },
    { label: 'Ilustração vetorial', value: 'Ilustração vetorial' },
    { label: 'Estilo sketch/desenho à mão', value: 'Estilo sketch/desenho à mão' },
    { label: 'Pixel art', value: 'Pixel art' },
    { label: 'Arte abstrata', value: 'Arte abstrata' },
    { label: 'Estilo steampunk', value: 'Estilo steampunk' },
    { label: 'Arte pop', value: 'Arte pop' },
    { label: 'Arte renascentista', value: 'Arte renascentista' },
    { label: 'Estilo cyberpunk', value: 'Estilo cyberpunk' },
    { label: 'Mangá/anime', value: 'Mangá/anime' },
  ]

  private perspectiveChipOptions: IFormInputOption[] = [
    { label: 'Frente', value: 'Frente' },
    { label: 'Diagonal', value: 'Diagonal' },
    { label: 'Close-up', value: 'Close-up' }
  ]
  private perspectiveOptions: IFormInputOption[] = [
    { label: 'Vista superior (top-down)', value: 'Vista superior (top-down)' },
    { label: 'Primeira pessoa (perspectiva do usuário)', value: 'Primeira pessoa (perspectiva do usuário)' },
    { label: 'Vista lateral (perfil)', value: 'Vista lateral (perfil)' },
    { label: 'Grande angular (captura de grande parte do cenário)', value: 'Grande angular (captura de grande parte do cenário)' },
    { label: 'Close-up (detalhe próximo de um objeto ou personagem)', value: 'Close-up (detalhe próximo de um objeto ou personagem)' },
    { label: 'Panorâmica (visão ampla horizontal)', value: 'Panorâmica (visão ampla horizontal)' },
    { label: 'Vista em 45° (ângulo inclinado, diagonal)', value: 'Vista em 45°' },
    { label: 'Vista aérea (olhando de cima como de um drone)', value: 'Vista aérea' },
    { label: 'Olho de peixe (distorção côncava ou convexa)', value: 'Olho de peixe' },
    { label: 'Perspectiva isométrica (ângulo 3D fixo sem deformação de profundidade)', value: 'Perspectiva isométrica' }
  ]
  private aspectRatioOptions: IFormInputOption[] = aspectRatioData

  private selectedAspectRatio: string = '1:1'
  private aspectRatio: string

  constructor(
    private service: IImagineService,
    private zeusClient: ZeusClient,
    private authService: IAuthService,
    private dialogService: IDialogService,
  ) { }

  async attached() {
    await this.getBrandInfo()
    await this.getLastGenerations()
  }

  async getBrandInfo() {
    const response = await this.zeusClient.chain('query')({
      brands: [
        {
          limit: 1
        },
        {
          id: true,
          brandName: true,
          segment: true,
          brandTargetAudience: true,
          accentColor: true,
          backgroundColor: true,
          foregroundColor: true
        }
      ]
    })

    if (!response.brands) {
      this.colorPalette = this.colorPalettiesChipOptions.find((cp) => cp.label == 'Minha marca')
      toastifyError({ message: "Não foi possível encontrar sua marca!" })
      return
    }

    this.brand = response.brands.at(0) as ModelTypes['brands']

    this.colorPalettiesChipOptions = [
      { label: 'Minha marca', value: `${this.brand.accentColor} ${this.brand.backgroundColor} ${this.brand.foregroundColor}` },
      { label: 'Tons quentes', value: 'Tons quentes' },
      { label: 'Monocromático', value: 'Monocromático' }
    ]

    this.colorPalette = this.colorPalettiesChipOptions.find((cp) => cp.label == 'Minha marca')
  }

  async generateImageIdea() {
    try {

      this.isLoading = true

      const response = await this.zeusClient.chain('query')({
        generateImageIdea: [
          {
            params: {
              brandName: this.brand.brandName,
              segment: this.brand.segment,
              brandTargetAudience: this.brand.brandTargetAudience
            }
          },
          {
            idea: true
          }
        ]
      })

      if (!response.generateImageIdea) throw new Error('Não foi possível gerar descrição de post')

      this.imagePrompt = response.generateImageIdea.idea
    } catch (e) {
      toastifyError({ message: e })
    } finally {
      this.isLoading = false
    }
  }

  async getLastGenerations() {
    const response = await this.zeusClient.chain('query')({
      userMedia: [
        {
          where: {
            _and: [
              {
                deleted: {
                  _eq: false
                }
              },
              {
                aiParameters: {
                  _is_null: false
                }
              }
            ]
          },
          order_by: [
            { created_at: order_by.desc }
          ],
          limit: 20
        },
        {
          id: true,
          aiParameters: [{ path: '$' }, true],
          created_at: true,
          fileId: true
        }
      ]
    })

    if (!response?.userMedia) return

    this.medias = response.userMedia as ModelTypes['userMedia'][]
  }

  private async handleGenerate(): Promise<void> {
    if (!['user-premium', 'user-freetrial', 'copymagico-admin'].includes(this.authService.user?.defaultRole)) {
      await this.dialogService.open({
        component: () => GoPremiumDialog,
        model: {},
        lock: true,
        startingZIndex: 100,
      });

      return
    }

    const spinnerDialog = await this.dialogService.open({
      component: () => FullScreenSpinnerDialog,
      model: {},
      lock: true,
      startingZIndex: 100,
    });

    try {
      const body = {
        colorPalette: this.colorPalette.value as string,
        artisticStyle: this.artisticStyle.value as string,
        perspective: this.perspective.value as string,
        prompt: this.imagePrompt,
        aspectRatio: this.selectedAspectRatio && this.selectedAspectRatio != 'others' ? this.selectedAspectRatio : this.aspectRatio
      }

      const { fileId, success } = await this.service.imagine(body);

      if (!success) {
        toastifyError({ message: 'Erro ao tentar gerar ilustração' });
        return;
      }
      GoogleAnalyticsHelper.trackEvent(GoogleAnalyticsEventAction.Click, GoogleAnalyticsEventCategory.Image, 'generate-illustration');

      this.ilustration = `https://uhgcmhbgmvuhtnaryxwr.storage.sa-east-1.nhost.run/v1/files/${fileId}`

      this.showLikeImage = true
      this.fileId = fileId

      await this.getLastGenerations()

      toastifySuccess({ message: 'Sua imagem foi salva em "Meus Documentos"' });
    } catch (error) {
      toastifyError({ message: error });
    } finally {
      spinnerDialog.dialog.ok();
    }
  }

  private async likeImage(like: boolean) {

    const response = await this.zeusClient.chain('query')({
      userMedia: [
        {
          where: {
            _and: [
              {
                fileId: {
                  _eq: this.fileId
                }
              }
            ]
          },
          order_by: [
            { created_at: order_by.desc }
          ],
          limit: 1
        },
        {
          id: true
        }
      ]
    })

    await this.zeusClient.chain('mutation')({
      update_userMedia_by_pk: [
        {
          pk_columns: {
            id: response.userMedia[0].id
          },
          _set: {
            isLikedImage: like
          }
        },
        {
          id: true
        }
      ]
    })

    this.showLikeImage = false
  }

  async openMediaHistory() {
    const { dialog } = await this.dialogService.open({
      component: () => MediaHistoryDrawer,
      lock: false,
      keyboard: ['Escape'],
      startingZIndex: 100,
      model: {
        medias: this.medias
      }
    })

    const { status, value } = await dialog.closed

    if (status == 'ok') {

      const convertedValue = value as ReuseParamsModel

      this.colorPalette = this.colorPalettiesChipOptions.find((c) => c.value == convertedValue.colorPalette) ?? this.colorPalettiesOptions.find((c) => c.value == convertedValue.colorPalette)

      this.artisticStyle = this.artisticStyleChipOptions.find((c) => c.value == convertedValue.artisticStyle) ?? this.artisticStyleOptions.find((c) => c.value == convertedValue.artisticStyle)

      this.perspective = this.perspectiveChipOptions.find((c) => c.value == convertedValue.perspective) ?? this.perspectiveOptions.find((c) => c.value == convertedValue.perspective)

      this.imagePrompt = convertedValue.prompt

      if (!['1:1', '4:3', '9:16'].includes(convertedValue.aspectRatio)) {
        this.selectedAspectRatio = 'others'
        setTimeout(() => {
          this.aspectRatio = convertedValue.aspectRatio
        }, 100)
      } else {
        this.selectedAspectRatio = convertedValue.aspectRatio
      }
    }
  }

  private searchColorPaletties(query: string) {
    return this.colorPalettiesOptions.filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }

  private searchArtisticStyles(query: string) {
    return this.artisticStyleOptions.filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }

  private searchPerspectives(query: string) {
    return this.perspectiveOptions.filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }
}